import dayjs from "dayjs";
import { useState, useEffect, useCallback, useContext } from "react";
import { DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { Form, Row, Spin, Select, Divider, Button } from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  getPagePermissions,
  EXPORT_REPORT_PERMISSION,
} from "../../../security/permissions";

import { useClient } from "hooks/useClient";
import {
  allowManualWarehouseLevel,
  ContainerResource,
  downloadVolumeMeasurementReport,
  getContainers,
  getVolumeMeasurerRegister,
  VolumeMeasurerRegistries,
} from "api/generated";
import useLoggedUser from "hooks/useLoggedUser";
import {
  handleAxiosError,
  handleCsvDownload,
} from "common/helpers/generalUtils";
import { useDateRange } from "context/DateRangeContext";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { RouteComponentProps, withRouter } from "react-router-dom";
import DistributionPointSelected from "context/MyDistributionPoint";
import ManualVolumeRegisterDrawer from "./ManualVolumeRegistrerDrawer";
import { ResponsiveTable } from "common/components/table/ResponsiveTable";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { FormItem } from "common/components/form/FormItem";

const columns: ColumnsType<VolumeMeasurerRegistries> = [
  {
    title: "Clave auditable del SAT por contenedor",
    dataIndex: "container",
    render: (_, record) => <div>{record.container?.containerKey || "N/A"}</div>,
    width: "15%",
  },
  {
    title: "Fecha registro de medición",
    dataIndex: "measuredTime",
    render: (date: string) =>
      date ? dayjs(date).format("YYYY/MM/DD HH:mm:ss") : "-",
    width: "15%",
  },
  {
    title: "Porcentaje de volumen",
    dataIndex: "volumePercentage",
    render: (val: number) => `${val ?? 0} %`,
    width: "10%",
  },
  {
    title: "Volumen",
    dataIndex: "volume",
    render: (val: number) =>
      val ? Intl.NumberFormat("en", { notation: "standard" }).format(val) : "0",
    width: "10%",
  },
  {
    title: "Presión",
    dataIndex: "pressure",
    width: "10%",
  },
  {
    title: "Temperatura",
    dataIndex: "temperature",
    width: "10%",
  },
];

function VolumeMeasurementsScreen(props: RouteComponentProps) {
  const [form] = Form.useForm();
  const [loadingForm, setLoadingForm] = useState<boolean>(false);
  const [loadingContainers, setLoadingContainers] = useState<boolean>(false);
  const [containers, setContainers] = useState<ContainerResource[]>([]);
  const [allowManualWarehouseLevelUpdate, setAllowManualWarehouseLevelUpdate] =
    useState<boolean>(false);
  const [containerId, setContainerId] = useState<string>();

  // ESTADO PARA MEDICIONES MANUALES
  const [showAddMesurement, setShowAddMesurement] = useState(false);

  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);

  const { permissions } = useLoggedUser();

  const exportReportsPermissions = getPagePermissions(
    EXPORT_REPORT_PERMISSION,
    permissions,
  );

  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();

  const pagination = usePaginationTable<VolumeMeasurerRegistries>(props, {
    persistInUrl: true,
  });

  const fetchVolumeMeasurerRegistries = useCallback(() => {
    setLoadingForm(true);

    getVolumeMeasurerRegister({
      client,
      body: {
        volumeMeasurerRegisterDisplayPage: pagination.currentPage,
        volumeMeasurerRegisterDisplayAmount: pagination.pageSize,
        idContainer: containerId,
        filterStartDate: startDate,
        filterEndDate: endDate,
        distributionPointId: distributionPoint?.id ?? "",
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .catch((error) =>
        handleAxiosError(
          error,
          "No se pudieron cargar los registros de medición",
        ),
      )
      .finally(() => setLoadingForm(false));
  }, [
    containerId,
    client,
    endDate,
    startDate,
    pagination.currentPage,
    pagination.pageSize,
    distributionPoint,
  ]);

  useEffect(() => {
    allowManualWarehouseLevel({ client })
      .then(({ data }) => setAllowManualWarehouseLevelUpdate(data ?? false))
      .catch((err) =>
        handleAxiosError(
          err,
          "No se pudo obtener la configuracion de permitir agregar mediciones manuales",
        ),
      );
  }, [client]);

  useEffect(() => {
    setLoadingContainers(true);
    getContainers({
      client,
      path: {
        distributionPointId: distributionPoint?.id ?? "",
      },
    })
      .then(({ data }) => {
        setContainers(data ?? []);
      })
      .catch((err) =>
        handleAxiosError(err, "No se pudieron obtener los almacenes"),
      )
      .finally(() => setLoadingContainers(false));
  }, [client, distributionPoint]);

  useEffect(() => {
    fetchVolumeMeasurerRegistries();
  }, [fetchVolumeMeasurerRegistries]);

  const handleContainerChange = (value: string): void => {
    setContainerId(value);
  };

  function downloadCSV() {
    setLoadingForm(true);

    downloadVolumeMeasurementReport({
      client,
      body: {
        idContainer: containerId,
        filterStartDate: startDate,
        filterEndDate: endDate,
        distributionPointId: distributionPoint?.id ?? "",
      },
    })
      .then((response) => handleCsvDownload(response))
      .catch((err) => handleAxiosError(err, "No se pudo descargar el reporte"))
      .finally(() => {
        setLoadingForm(false);
      });
  }

  const handleManualRegisterRefresh = (): void => {
    form.resetFields();
    fetchVolumeMeasurerRegistries();
  };

  return (
    <Spin size="large" tip="Cargando..." spinning={loadingForm}>
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <ResponsiveCol>
            <FormItem
              label="Clave auditable de contenedor"
              name="idContainer"
              initialValue=""
            >
              <Select
                onChange={handleContainerChange}
                loading={loadingContainers}
              >
                <Select.Option value="">Todos</Select.Option>
                {containers.map((container) => (
                  <Select.Option
                    key={container.idContainer}
                    value={container.idContainer}
                  >
                    {container.containerKey}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol>
            <FormItem label="Seleccionar periodo">
              <DateRangePicker
                config={{
                  allowedTypes: [
                    DateFilterType.DAY,
                    DateFilterType.MONTH,
                    DateFilterType.RANGE,
                  ],
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={setRangeTimeValue}
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol>
            <FormItem className="transparent-label" label=" ">
              {exportReportsPermissions.read && (
                <Button
                  style={{ width: "100%" }}
                  type="primary"
                  icon={<FileExcelOutlined />}
                  disabled={loadingForm}
                  onClick={downloadCSV}
                >
                  Descargar CSV
                </Button>
              )}
            </FormItem>
          </ResponsiveCol>
        </Row>
        <Row>
          {exportReportsPermissions.read && allowManualWarehouseLevelUpdate && (
            <ResponsiveCol>
              <FormItem className="transparent-label" label=" ">
                <Button
                  style={{ width: "100%" }}
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    setShowAddMesurement(true);
                  }}
                >
                  Insertar registro de volumen a tanque
                </Button>
              </FormItem>
            </ResponsiveCol>
          )}
        </Row>
      </Form>

      <ResponsiveTable<VolumeMeasurerRegistries>
        columns={columns}
        scroll={{ x: true }}
        rowKey={(record) => record.id || Math.random().toString()}
        dataSource={pagination.items}
        pagination={{
          ...pagination.paginationConfig,
          onChange: pagination.onPageChange,
          onShowSizeChange: pagination.onPageSizeChange,
        }}
        loading={loadingForm}
      />
      <Divider />

      {showAddMesurement && (
        <ManualVolumeRegisterDrawer
          isVisible={showAddMesurement}
          onClose={() => {
            setShowAddMesurement(false);
            handleManualRegisterRefresh();
          }}
        />
      )}
    </Spin>
  );
}

export default withRouter(VolumeMeasurementsScreen);
